import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/feature/BackToTop';
import TickerBar from '@/components/feature/TickerBar';
import HomeHero from './components/HomeHero';
import TodaySection from './components/TodaySection';
import WeeklySection from './components/WeeklySection';
import ScheduleSection from './components/ScheduleSection';
import HotRankSection from './components/HotRankSection';
import CategorySection from './components/CategorySection';
import TestimonialSection from './components/TestimonialSection';
import FaqPreview from './components/FaqPreview';
import SubscribeSection from './components/SubscribeSection';
import { usePageMeta } from '@/hooks/usePageMeta';
import { articles } from '@/mocks/articles';
import { faqs } from '@/mocks/faqs';
import { site } from '@/mocks/site';
import type { Article, FaqItem } from '@/types/content';

const anchors = [
  { id: 'today', label: '今日上新' },
  { id: 'weekly', label: '本周推荐' },
  { id: 'schedule', label: '档期专区' },
  { id: 'hot', label: '热点榜' },
  { id: 'categories', label: '主题分类' },
  { id: 'reviews', label: '读者评价' },
  { id: 'faq', label: '常见问题' },
];

export default function Home() {
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) {
      const timer = window.setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 80);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [location.hash]);

  const tickerItems = useMemo(
    () => [...(articles as Article[])].sort((a, b) => b.heat - a.heat).slice(0, 6).map((a) => ({ id: a.id, title: a.title })),
    [],
  );

  const schema = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: site.brand,
        url: 'https://coulex.cn/',
        description: '按档期整理娱乐热点与福利内容的吃瓜门户',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://coulex.cn/topics?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: site.brand,
        url: 'https://coulex.cn/',
        email: site.email,
        description: site.intro,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: '福利吃瓜 - 档期内容导航',
        url: 'https://coulex.cn/',
        description: '今日上新、本周推荐、周末限定、假日特辑与近期热点集中呈现。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: (faqs as FaqItem[]).slice(0, 4).map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
    [],
  );

  usePageMeta({
    title: '福利吃瓜 - 今日上新·本周推荐·周末限定娱乐热点一站看',
    description:
      '福利吃瓜按档期整理娱乐热点与福利内容，覆盖今日上新、本周推荐、周末限定、假日特辑与近期热点，帮你快速跟上节奏，不错过任何一场精彩好戏。',
    keywords: '福利吃瓜,福利吃瓜今日上新,福利吃瓜本周推荐,福利吃瓜周末限定,福利吃瓜假日特辑,福利吃瓜近期热点',
    path: '/',
    schema,
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader overlay />
      <main>
        <HomeHero />
        <TickerBar items={tickerItems} />

        <nav
          aria-label="页面板块导航"
          className="sticky top-16 md:top-20 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200"
        >
          <div className="w-full px-4 md:px-8 lg:px-12">
            <ul className="flex items-center gap-1 overflow-x-auto no-scrollbar py-2.5">
              {anchors.map((a) => (
                <li key={a.id}>
                  <a
                    href={`#${a.id}`}
                    className="inline-block px-3.5 py-2 rounded-md text-sm text-foreground-600 hover:text-primary-600 hover:bg-primary-50 whitespace-nowrap transition-colors"
                  >
                    {a.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        <TodaySection />
        <WeeklySection />
        <ScheduleSection />
        <HotRankSection />
        <CategorySection />
        <TestimonialSection />
        <FaqPreview />
        <SubscribeSection />
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}